import React, {useEffect, useRef, useState} from "react";
import {useTranslation} from "next-i18next";
import {useAppDispatch, useAppSelector} from "../../app/hooks";
import {fetchStore, fetchStoreSucceeded, reset, selectCart, selectStore} from "../../redux/store/storeSlice";
import {
  fetchStoreSectionForCategoryId, fetchStoreSectionForId,
  fetchStoreSectionSucceeded,
  selectError,
  selectIsLoading,
  selectStoreSection
} from "../../redux/storeSectionDetail/storeSectionDetailSlice";
import {StoreSectionModel} from "../../models/storeSection";
import {ProductModel} from "../../models/product";
import {ProductSkuModel} from "../../models/productSku";
import {setAddedCartItem} from "../../redux/navigation/navigationSlice";
import {fetchCart} from "../../redux/cart/cartSlice";
import {StoreEmptyStateView} from "../storeFeed/StoreEmptyStateView";
import {GroceryColorIcon, WarningColorIcon} from "../icons";
import {Container} from "../containers/Container";
import {Body, HeroHeading2} from "../text";
import Skeleton from "react-loading-skeleton";
import {StoreSectionCarouselView} from "../store/StoreSectionCarouselView";
import {CartButton} from "../buttons/CartButton";
import {CurrencyUtils} from "../../utils/currencyUtils";
import {ImageUtils} from "../../utils/imageUtils";
import {Image} from "../images/Image";
import {goBack} from "connected-next-router";
import {useRouter} from "next/router";
import {useContextualRouting} from "next-use-contextual-routing";
import {NavBarNegativeHeight} from "../navBars/NavBarMain";
import {EventLogger} from "../../utils/eventLogger";
import {AnalyticsEvent} from "../../utils/eventProperties";
import Link from 'next/link'
import {useModalRouting} from "../../utils/useModalRouting";

export interface StoreSectionDetailViewProps {
  categoryId?: string
  sectionId?: string
  sectionType: 'category' | 'section'
  storeSection?: StoreSectionModel
}

export const StoreSectionDetailView: React.FC<StoreSectionDetailViewProps> = ({ categoryId, sectionId, sectionType, storeSection: preloadedStoreSection }) => {
  const { t } = useTranslation()
  const router = useRouter()
  const { makeContextualHref } = useContextualRouting()
  const dispatch = useAppDispatch();
  const storeSection = useAppSelector(selectStoreSection)
  const isLoading = useAppSelector(selectIsLoading)
  const cart = useAppSelector(selectCart)
  const error = useAppSelector(selectError)
  const [loading, setLoading] = useState(isLoading)

  const cartTotal = CurrencyUtils.stringFromPrice(cart?.totalPrice, cart?.currencyCode)
  const cartTitle = t('store:store_button_view_cart')
  const cartQuantity = cart?.items.length.toString() ?? "0"

  const category = storeSection?.categories[0]

  const timeout = useRef<number>();
  const [cartToolbarHidden, setCartToolbarHidden] = useState(true)

  const { createRoute } = useModalRouting();

  let products = storeSection?.products
  if (loading || !storeSection) {
    products = Array.from({ length: products?.length ?? 30 }).map(() => ProductModel.fromJson({ name: 'Default', skus: [ProductSkuModel.fromJson({ price: 0 })!] })!)
  }

  useEffect(() => {
    EventLogger.logEvent(new AnalyticsEvent({ eventName: 'Screen Visited', screenName: 'Store Detail' }))
  }, [])

  useEffect(() => {
    const cartIsEmpty = !cart || cart.items.length <= 0

    timeout.current = window.setTimeout(() => {
      setCartToolbarHidden(cartIsEmpty)
    }, 200);
  }, [cart])

  useEffect(() => {
    dispatch(setAddedCartItem())
    dispatch(reset())
    if (preloadedStoreSection) {
      dispatch(fetchStoreSectionSucceeded(preloadedStoreSection))
    }

    if (categoryId) {
      dispatch(fetchStoreSectionForCategoryId(categoryId))
    } else if (sectionId) {
      dispatch(fetchStoreSectionForId(sectionId))
    }
  }, [categoryId, sectionId])

  useEffect(() => {
    if (storeSection?.storeId) {
      dispatch(fetchCart(storeSection.storeId))
    }
  }, [storeSection])

  useEffect(() => {
    setLoading(!storeSection || isLoading)
  }, [storeSection, isLoading])

  if (error) {
    return <div className={`h-screen flex flex-col justify-center items-center ${NavBarNegativeHeight}`}>
      <StoreEmptyStateView
        onClick={() => {
          if (categoryId) {
            dispatch(fetchStoreSectionForCategoryId(categoryId))
          } else if (sectionId) {
            dispatch(fetchStoreSectionForId(sectionId))
          }

          if (storeSection?.storeId) {
            dispatch(fetchCart(storeSection.storeId))
          }
        }}
        isLoading={isLoading}
        icon={<WarningColorIcon />}
        title={error.localizedTitle}
        subtitle={error.localizedMessage}
        buttonTitle={t('store:store_empty_state_button_try_again')}
      />
    </div>
  } else if (!loading && (!storeSection || storeSection.products.length <= 0) && sectionType === 'category') {
    return <div className={`h-screen flex flex-col justify-center items-center ${NavBarNegativeHeight}`}>
      <StoreEmptyStateView
        onClick={() => {
          dispatch(goBack())
        }}
        isLoading={isLoading}
        icon={
          (() => {
            const imageUrl = ImageUtils.createUrlFromImage(category?.image)

            if (imageUrl) {
              return <Image
                src={imageUrl}
                objectFit="contain"
                sizeClass="h-36 w-36 lg:h-44 lg:w-44"
                alt={category?.name}
              />
            } else {
              return <GroceryColorIcon />
            }
          })()
        }
        title={t('store:store_empty_state_title_no_products_available', { category: category?.name })}
        subtitle={t('store:store_empty_state_message_no_products_available')}
        buttonTitle={t('store:store_empty_state_button_go_back')}
      />
    </div>
  }

  return (
    <div>
      <Container>
        <div className="mx-2 md:mx-4 lg:mx-6">
          <div className="">
            <HeroHeading2>{loading ? <Skeleton width="30%" /> : storeSection?.title}</HeroHeading2>
            <Body className="mt-2 text-fg-gray-dark">{loading ? <Skeleton width="50%" /> : storeSection?.subtitle}</Body>
          </div>

          <div className="max-w-full mt-8 mb-24 h-full">
            <StoreSectionCarouselView products={products ?? []} carouselType="grid" loading={loading} />
          </div>
        </div>
      </Container>

      <div className={`fixed z-20 w-full bottom-6 lg:hidden transition-all transform ease-in-out duration-500 px-4 sm:px-8 lg:px-10 max-w-screen-2xl ${cartToolbarHidden ? 'opacity-0 translate-y-12' : 'opacity-100 translate-y-0'}`}>
        <div className="mx-2 md:mx-4 lg:mx-6 rounded-full shadow-lg">
          <Link
            href={createRoute({ name: 'cart', query: { cart_id: storeSection?.storeId } })}
            as={`/cart/${storeSection?.storeId}`}
            shallow={true}
            passHref
          >
            <a>
              <CartButton
                total={cartTotal}
                buttonTitle={cartTitle}
                quantity={cartQuantity}
              />
            </a>
          </Link>
        </div>
      </div>
    </div>
  )
}